import React, { useState, useEffect, useMemo } from 'react';
import { LuPlus, LuUpload } from 'react-icons/lu';
import SafeIcon from '../common/SafeIcon';
import AccountListTable from '../components/accounts/AccountListTable';
import AccountFilters from '../components/accounts/AccountFilters';
import CreateAccountModal from '../components/accounts/CreateAccountModal';
import AccountDetailsModal from '../components/accounts/AccountDetailsModal';
import AccountStats from '../components/accounts/AccountStats';
import ImportCsvModal from '../components/common/ImportCsvModal';
import { accountService } from '../services/accountService';
import { useAuth } from '../hooks/useAuth';

const Accounts = () => {
  const { userProfile } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIndustry, setSelectedIndustry] = useState('');

  // Modal States
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      const result = await accountService.getAccounts({ orderBy: 'name', ascending: true });
      if (!result.success) {
        setError(result.message);
        setAccounts([]);
      } else {
        setAccounts(result.data);
        setError('');
      }
    } catch (err) {
      setError('Failed to fetch accounts');
      console.error('Error fetching accounts:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAccount = async (formData) => {
    try {
      setActionLoading(true);
      const result = await accountService.createAccount(formData);
      if (!result.success) {
        setError(result.message);
        return;
      }
      setIsCreateModalOpen(false);
      await fetchAccounts();
    } catch (err) {
      setError('Failed to create account');
      console.error('Error creating account:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateAccount = async (id, formData) => {
    try {
      setActionLoading(true);
      const result = await accountService.updateAccount(id, formData);
      if (!result.success) {
        setError(result.message);
        return;
      }
      // Update local state immediately for better UX
      setAccounts(prev => prev.map(acc => acc.id === id ? result.data : acc));
      setSelectedAccount(result.data); // Update the modal data
      // await fetchAccounts(); // Optional: Fetch fresh data
    } catch (err) {
      setError('Failed to update account');
      console.error('Error updating account:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteAccount = async (id) => {
    try {
      setActionLoading(true);
      const result = await accountService.deleteAccount(id);
      if (!result.success) {
        setError(result.message);
        return;
      }
      setIsDetailsModalOpen(false);
      setSelectedAccount(null);
      await fetchAccounts(); // Refresh list to remove soft-deleted account
    } catch (err) {
      setError('Failed to delete account');
      console.error('Error deleting account:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleImportComplete = (results) => {
    // Refresh accounts list after import
    fetchAccounts();
  };

  const handleAccountClick = (account) => {
    setSelectedAccount(account);
    setIsDetailsModalOpen(true);
  };

  const filteredAccounts = useMemo(() => {
    return accounts.filter(account => {
      const matchesSearch = account.name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesIndustry = !selectedIndustry || account.industry === selectedIndustry;
      return matchesSearch && matchesIndustry;
    });
  }, [accounts, searchTerm, selectedIndustry]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Accounts</h2>
          <p className="mt-1 text-sm text-gray-600">
            Manage customer accounts shared across all teams
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setIsImportModalOpen(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
          >
            <SafeIcon icon={LuUpload} className="h-4 w-4" />
            <span>Import CSV</span>
          </button>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
          >
            <SafeIcon icon={LuPlus} className="h-4 w-4" />
            <span>New Account</span>
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-lg">
          <div className="flex">
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Stats */}
      <AccountStats accounts={accounts} />

      {/* Filters */}
      <AccountFilters
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        industry={selectedIndustry}
        onIndustryChange={setSelectedIndustry}
      />

      {/* Results Count */}
      {!loading && (
        <div className="flex items-center justify-between text-sm text-gray-600">
          <span>
            Showing {filteredAccounts.length} of {accounts.length} accounts
          </span>
          {(searchTerm || selectedIndustry) && (
            <button
              onClick={() => { setSearchTerm(''); setSelectedIndustry(''); }}
              className="text-blue-600 hover:text-blue-700"
            >
              Clear filters
            </button>
          )}
        </div>
      )}

      {/* Accounts Table */}
      <AccountListTable
        accounts={filteredAccounts}
        loading={loading}
        onAccountClick={handleAccountClick}
      />

      {/* Create Account Modal */}
      <CreateAccountModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreateAccount}
        loading={actionLoading}
      />

      {/* Account Details Modal */}
      <AccountDetailsModal
        isOpen={isDetailsModalOpen}
        onClose={() => setIsDetailsModalOpen(false)}
        account={selectedAccount}
        onUpdate={handleUpdateAccount}
        onDelete={handleDeleteAccount}
        loading={actionLoading}
        canDelete={userProfile?.role === 'Admin'} // Only Admins can delete
      />

      {/* Import CSV Modal */}
      <ImportCsvModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        entityType="accounts"
        onImportComplete={handleImportComplete}
      />
    </div>
  );
};

export default Accounts;